import { Component, type ErrorInfo, type PropsWithChildren } from 'react';
import Box from '@common/components/Box';
import Container from '@common/components/Container';
import Button from '@common/components/controls/Button';
import Icon from '@common/components/Icon';
import IconError from '@common/components/svgs/common/Error';
import Translated from '@common/components/Translated';
import analytics from '@common/utils/analytics';
import sessionStorageManager from '@common/utils/storage/sessionStorageManager';
import { SESSION_ERROR_RECOVERY_STATE_KEY } from '@common/utils/storage/storageKeys';
import '@common/styles/components/error.pcss';

type ErrorRecoveryState = 'tryReload' | 'reloadComplete';

class ErrorBoundary extends Component<PropsWithChildren<{}>> {
  static getDerivedStateFromError() {
    const state = sessionStorageManager.getItem<ErrorRecoveryState>(
      SESSION_ERROR_RECOVERY_STATE_KEY,
    );
    if (!state) {
      sessionStorageManager.saveItem<ErrorRecoveryState>(
        SESSION_ERROR_RECOVERY_STATE_KEY,
        'tryReload',
      );
    }
  }

  componentDidMount() {
    const state = sessionStorageManager.getItem<ErrorRecoveryState>(
      SESSION_ERROR_RECOVERY_STATE_KEY,
    );
    if (state === 'reloadComplete') {
      sessionStorageManager.removeItem(SESSION_ERROR_RECOVERY_STATE_KEY);
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo = {}) {
    const state = sessionStorageManager.getItem<ErrorRecoveryState>(
      SESSION_ERROR_RECOVERY_STATE_KEY,
    );
    if (state === 'tryReload') {
      import('@common/utils/analytics/applicationInsights').then((module) => {
        module.default?.trackException({ exception: error });
      });
    } else {
      analytics.captureEvent('ErrorScreenPresented', {
        error: JSON.stringify(error, Object.getOwnPropertyNames(error)),
        ...errorInfo,
      });
    }
    window.location.reload();
    sessionStorageManager.saveItem<ErrorRecoveryState>(
      SESSION_ERROR_RECOVERY_STATE_KEY,
      'reloadComplete',
    );
  }

  render() {
    const state = sessionStorageManager.getItem<ErrorRecoveryState>(
      SESSION_ERROR_RECOVERY_STATE_KEY,
    );
    if (!state) {
      const { children } = this.props;
      return children; // normal view
    }
    if (state === 'tryReload') {
      return null; // componentDidCatch handles reload as needed
    }

    return (
      <div className="container">
        <Container className="error">
          <Icon component={IconError} />
          <Box my={4} tagName="h2">
            <Translated id="error-view.title" />
          </Box>
          <Translated id="error-view.description" tagName="p" />
          <Box mt={8}>
            <Button
              onClick={() => window.location.reload()}
              data-testid="refresh-button"
            >
              <Translated id="error-view.action" />
            </Button>
          </Box>
        </Container>
      </div>
    );
  }
}

export default ErrorBoundary;
